namespace appn.designerv2 {
    
    export function getPropertyByPath(obj: any, path: string): any {
        return path.split('.').reduce((o, k) => (o || {})[k], obj);
    }

    export function setPropertyByPath(obj: any, path: string, value: any) {
        const keys = path.split('.');
        let current = obj;
        for (let i = 0; i < keys.length - 1; i++) {
            if (current[keys[i]] === undefined || current[keys[i]] === null)
                current[keys[i]] = {};

            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
    }

    /* Attach a preventative click handler to a custom checkbox to stop focus-related scrolling. */
    export function hookCheckboxHandler($editor: JQuery, checkboxSelector: string, onCheckedChanged: (isChecked: boolean) => void): void {
        const $checkbox = $editor.find(checkboxSelector);
        if (!$checkbox.length)
            return;

        /**
         * Prevents the default click behavior to stop the browser from focusing on the hidden
         * input and causing an unwanted scroll jump. Since the input's state is no longer
         * toggled automatically, we manually invert its current state for the callback.
         */
        $checkbox.closest('.icheck-primary.appn-checkbox').on('click', (e) => {
            e.preventDefault(); // Prevent native browser focus/scroll behavior

            const isCurrentlyChecked = $checkbox.prop('checked');
            const newState = !isCurrentlyChecked;

            onCheckedChanged(newState);
        });
    }
    
    function findDiff(prev: any, current: any, path = '', changes = {}): { [path: string]: { type: 'added' | 'deleted' | 'changed', oldValue?: any, newValue?: any }} {
        const allKeys = new Set([...Object.keys(prev), ...Object.keys(current)]);
        const isParentArray = Array.isArray(prev);
        for (const key of allKeys) {
            // Determine the path structure: dot notation for objects, index notation for arrays.
            const currentPath = isParentArray ? `${path}[${key}]` : (path ? `${path}.${key}` : key);
            const prevValue = prev[key];
            const currentValue = current[key];
            const prevType = typeof prevValue;
            const currentType = typeof currentValue;
            const isPrevObject: boolean = prevType === 'object' && prevValue !== null;
            const isCurrentObject: boolean = currentType === 'object' && currentValue !== null;
            const isPrevArray: boolean = Array.isArray(prevValue);
            const isCurrentArray: boolean = Array.isArray(currentValue);
            if (!(key in current)) {
                // Deleted: Key exists in previous but not current
                changes[currentPath] = { type: 'deleted', oldValue: prevValue };
                continue;
            }

            if (!(key in prev)) {
                // Added: Key exists in current but not previous
                changes[currentPath] = { type: 'added', newValue: currentValue };
                continue;
            }

            // Recursion Logic: Recurse if both are objects AND they are of the same container type
            if (isPrevObject && isCurrentObject) {
                // Recurse if both are arrays OR both are plain objects (not arrays)
                if ((isPrevArray && isCurrentArray) || (!isPrevArray && !isCurrentArray)) {
                    findDiff(prevValue, currentValue, currentPath, changes);
                    continue;
                }
            }

            // Final value comparison for primitives, mixed types (e.g., array changed to object), or differing non-recursed objects
            // We use JSON.stringify for a simple deep comparison of values
            if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                // Changed: Value is different
                changes[currentPath] = { type: 'changed', oldValue: prevValue, newValue: currentValue };
            }
        }
        
        return changes;
    }

    export function logDiffToConsole(prev: any, current: any) {
        const diff = findDiff(prev, current);
        const changeKeys = Object.keys(diff);

        if (changeKeys.length === 0) {
            console.log('%c[Diff Logger] No changes detected.', 'color: #10b981; font-weight: bold;');
            return;
        }

        console.group(`%c[Diff Logger] State Change Detected: ${changeKeys.length} changes`,
            'color: #4f46e5; font-weight: bold; background: #eef2ff; padding: 4px 6px; border-radius: 4px;'
        );

        for (const path of changeKeys) {
            const { type, oldValue, newValue } = diff[path];

            let logStyle: string = '';
            let typeLabel: string = '';
            switch (type) {
                case 'added':
                    logStyle = 'color: #10b981; font-weight: bold;'; // green
                    typeLabel = 'ADDED';
                    // Single-line output: ADDED: path (New Value: "...")
                    console.log(`%c${typeLabel}: ${path} (New Value: ${JSON.stringify(newValue, null, '  ')})`, logStyle);
                    break;
                case 'deleted':
                    logStyle = 'color: #ef4444; font-weight: bold;'; // red
                    typeLabel = 'DELETED';
                    // Single-line output: DELETED: path (Old Value: "...")
                    console.log(`%c${typeLabel}: ${path} (Old Value: ${JSON.stringify(oldValue, null, '  ')})`, logStyle);
                    break;
                case 'changed':
                    logStyle = 'color: #f97316; font-weight: bold;'; // orange
                    typeLabel = 'CHANGED';
                    // Single-line output: CHANGED: path: "..." -> "..."
                    console.log(`%c${typeLabel}: ${path}: ${JSON.stringify(oldValue, null, '  ')} -> ${JSON.stringify(newValue, null, '  ')}`, logStyle);
                    break;
            }
        }
        console.groupEnd();
    }
}
